import { Autocomplete, Section } from '@armhr/ui';
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useFormikContext } from 'formik';
import { useEffect, useMemo, useState } from 'react';

import { nhqFieldKey } from '../../utils/newHireQuestionsForm';

const QuestionField = ({ question }: { question: NewHireQuestion }) => {
  const formik = useFormikContext<any>();
  const fieldKey = nhqFieldKey(question.id);
  const value = formik.values[fieldKey] ?? '';
  const error = formik.errors[fieldKey] as string | undefined;
  const touched = formik.touched[fieldKey];
  const showError = !!error && !!touched;

  const handleChange = (newValue: string) => {
    formik.setFieldValue(fieldKey, newValue);
    formik.setFieldTouched(fieldKey, true, false);
  };

  switch (question.type) {
    case 'D': // Date
      return (
        <TextField
          name={fieldKey}
          label={question.text}
          type="date"
          fullWidth
          required={question.isMandatory}
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={formik.handleBlur}
          error={showError}
          helperText={showError ? error : undefined}
          InputLabelProps={{ shrink: true }}
        />
      );

    case 'N': // Number
      return (
        <TextField
          name={fieldKey}
          label={question.text}
          type="number"
          fullWidth
          required={question.isMandatory}
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={formik.handleBlur}
          error={showError}
          helperText={showError ? error : undefined}
        />
      );

    case 'T': // Text
      return (
        <TextField
          name={fieldKey}
          label={question.text}
          fullWidth
          required={question.isMandatory}
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={formik.handleBlur}
          error={showError}
          helperText={showError ? error : undefined}
        />
      );

    case 'C': // Checkbox (0/1)
      return (
        <FormControl error={showError}>
          <FormControlLabel
            control={
              <Checkbox
                name={fieldKey}
                checked={value === '1'}
                onChange={(e) => handleChange(e.target.checked ? '1' : '0')}
              />
            }
            label={
              <>
                {question.text}
                {question.isMandatory && (
                  <Typography component="span" color="error">
                    {' '}
                    *
                  </Typography>
                )}
              </>
            }
          />
          {showError && <FormHelperText>{error}</FormHelperText>}
        </FormControl>
      );

    case 'I': // Radio button
      return (
        <FormControl error={showError} required={question.isMandatory}>
          <FormLabel>{question.text}</FormLabel>
          <RadioGroup
            name={fieldKey}
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            row
          >
            {question.options?.map((opt) => (
              <FormControlLabel
                key={opt.code ?? opt.description}
                value={opt.code ?? opt.description ?? ''}
                control={<Radio />}
                label={opt.description ?? opt.code ?? ''}
              />
            ))}
          </RadioGroup>
          {showError && <FormHelperText>{error}</FormHelperText>}
        </FormControl>
      );

    case 'R': // Dropdown
      return (
        <Autocomplete
          name={fieldKey}
          label={question.text}
          fullWidth
          required={question.isMandatory}
          value={value}
          options={(question.options ?? []).map((opt) => ({
            value: opt.code ?? opt.description ?? '',
            label: opt.description ?? opt.code ?? '',
          }))}
          onChange={(newValue) => handleChange(newValue || '')}
          error={showError}
          helperText={showError ? error : undefined}
        />
      );

    default:
      // Fallback to text for unknown types
      return (
        <TextField
          name={fieldKey}
          label={question.text}
          fullWidth
          required={question.isMandatory}
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={formik.handleBlur}
          error={showError}
          helperText={showError ? error : undefined}
        />
      );
  }
};

const NewHireQuestionsFields = ({
  questions,
  loading,
  stateCode,
  showOptional,
}: {
  questions: NewHireQuestion[];
  loading: boolean;
  stateCode?: string;
  showOptional: boolean;
}) => {
  const hasMandatory = useMemo(
    () => questions.some((q) => q.isMandatory),
    [questions]
  );

  const [showOptionalFields, setShowOptionalFields] = useState(
    hasMandatory ? showOptional : false
  );

  useEffect(() => {
    // When questions change (e.g. new location selected), default to
    // collapsed unless there are mandatory questions.
    setShowOptionalFields(hasMandatory ? showOptional : false);
  }, [showOptional, hasMandatory]);

  const optionalCount = useMemo(
    () => questions.filter((q) => !q.isMandatory).length,
    [questions]
  );

  const visibleQuestions = useMemo(
    () =>
      showOptionalFields ? questions : questions.filter((q) => q.isMandatory),
    [questions, showOptionalFields]
  );

  if (!loading && questions.length === 0) {
    return null;
  }

  const title = stateCode
    ? `New Hire Questions for ${stateCode} (${questions.length})`
    : 'New Hire Questions';

  const toggleLink =
    optionalCount > 0 ? (
      <Typography
        onClick={() => setShowOptionalFields((prev) => !prev)}
        fontSize={14}
        color="primary"
        sx={{ cursor: 'pointer' }}
      >
        {showOptionalFields
          ? `Hide ${optionalCount} optional fields`
          : `Show ${optionalCount} optional fields`}
      </Typography>
    ) : null;

  return (
    <Section title={title} description={<Box>{toggleLink}</Box>} vertical>
      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Loading questions for this location...
          </Typography>
        </Box>
      ) : visibleQuestions.length === 0 ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" color="gray">
            No fields are required
          </Typography>
        </Box>
      ) : (
        <Stack spacing={3}>
          {visibleQuestions.map((question) => (
            <Box key={question.id}>
              <QuestionField question={question} />
            </Box>
          ))}
        </Stack>
      )}
    </Section>
  );
};

export default NewHireQuestionsFields;
